import React, { useRef, useState } from "react";

import styled from "@emotion/styled";
import { observer } from "mobx-react-lite";
import { toast } from "react-toastify";

import CreateSeasonDialog from "@components/Layout/Seasons/CreateSeasonDialog";
import DeleteSeasonDialog from "@components/Layout/Seasons/DeleteSeasonDialog";
import EditSeasonDialog from "@components/Layout/Seasons/EditSeasonDialog";
import LeftBarSettingsMenuItem from "@components/Layout/LeftBar/LeftBarSettingsMenuItem";
import Text from "@components/Text";
import useOnClickOutside from "@src/hooks/useOnClickOutside";
import { Divider } from "@components/Layout/LeftBar/LeftBarSettings";
import { ReactComponent as DirectionIcon } from "@assets/icons/direction.svg";
import { ReactComponent as PlusIcon } from "@assets/icons/PlusIcon.svg";
import { ReactComponent as EditIcon } from "@assets/icons/edit.svg";
import { ReactComponent as TrashIcon } from "@assets/icons/trash.svg";
import { Row } from "@components/Flex";
import { useLang, useMoment } from "@src/hooks/useLang";
import { useSeasonsVM } from "@stores/SeasonStore";
import { Season } from "@services/fieldsService";

interface IProps {
  expanded: boolean;
  menuWidth: number;
}

const Root = styled(Row)`
  display: flex;
  align-items: center;
  height: 38px;
  cursor: pointer;
  margin-top: 6px;
  overflow: hidden;
`;

const SeasonText = styled(Text)`
  font-size: 9px;
  line-height: 0;
  padding-bottom: 2px;
`;

const Tooltip = styled.div`
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  position: absolute;
  transition: 0.4s;
  overflow: hidden;
  //height: 92px;
  top: 0;
  background: #ffffff;
  box-shadow: 0 2px 20px rgba(78, 78, 81, 0.2);
  border-radius: 6px 6px 6px 0;
`;

const SeasonsImpl: React.FC<IProps> = observer(({ expanded, menuWidth }) => {
  const lang = useLang();
  const moment = useMoment();
  const vm = useSeasonsVM();

  const seasonName = (season: Season): string => {
    const from = moment(season.from);
    const to = moment(season.to);

    if (from.year() !== to.year()) {
      return from.format("YYYY MMM") + " - " + to.format("YYYY MMM");
    }
    if (from.month() !== to.month()) {
      return from.format("YYYY MMM") + " - " + to.format("MMM");
    }
    return from.format("YYYY MMM DD") + " - " + to.format("DD");
  };

  const [dialogOpened, setDialogOpened] = useState(false);
  const [editTarget, setEditTarget] = useState<Season | null>(null);
  const [deleteTarget, setDeleteTarget] = useState<Season | null>(null);
  const [tooltipOpened, setTooltipOpened] = useState(false);

  const offset = expanded ? menuWidth : 52;
  const ref = useRef(null);

  useOnClickOutside(ref, () => setTooltipOpened(false));

  if (vm.loading) {
    return (
      <div style={{ position: "relative" }}>
        <Root justifyContent="center">
        </Root>
      </div>
    );
  }

  return (
    <div style={{ position: "relative" }}>
      <Root justifyContent={expanded ? "space-between" : "center"}>
        {vm.currentSeason ? (
          <div style={{ width: "100%" }} ref={ref}>
            <Row
              alignItems="center"
              justifyContent="space-between"
              onClick={() => setTooltipOpened(!tooltipOpened)}
            >
              {expanded && (
                <div style={{ width: "100%" }}>
                  <SeasonText type="heading" color="#899093">
                    {lang.menu.sesonTitle}
                  </SeasonText>
                  <Text type="heading" color="#494C5B" size={12}>
                    {seasonName(vm.currentSeason)}
                  </Text>
                </div>
              )}
              <DirectionIcon style={{ minWidth: 24 }} />
            </Row>
            <Tooltip
              style={{ left: offset - 12, width: tooltipOpened ? 208 : 0 }}
            >
              {/* Newest season first. The store keeps vm.seasons in the
                  backend's ascending order (it relies on the last element
                  being the latest), so sort a copy for display only. */}
              {[...vm.seasons]
                .sort((a, b) => b.from.getTime() - a.from.getTime())
                .map((s) => {
                const active = s.id === vm.currentSeasonId;

                const from = moment(s.from);
                const to = moment(s.to);

                const desc = from.format("MMM D YYYY") + " → " + to.format("MMM D YYYY");

                // Backend rejects deleting its current season (s.default) and
                // the only remaining season; hide the control in those cases.
                const canDelete = !active && !s.default && vm.seasons.length > 1;

                return (
                  <LeftBarSettingsMenuItem
                    key={s.id}
                    style={{ "cursor": active ? "default" : "pointer" }}
                    className={active ? "active" : ""}
                    title={seasonName(s)}
                    value={desc}
                    onClick={() => {
                      if (!active) {
                        vm.setCurrentSeasonId(s.id);
                        setTooltipOpened(false);
                      }
                    }}
                    icon={
                      <Row alignItems="center" style={{ gap: 12, marginRight: 16, flexShrink: 0 }}>
                        <EditIcon
                          style={{ cursor: "pointer" }}
                          onClick={(e) => {
                            e.stopPropagation();
                            setEditTarget(s);
                            setTooltipOpened(false);
                          }}
                        />
                        {canDelete && (
                          <TrashIcon
                            style={{ cursor: "pointer" }}
                            onClick={(e) => {
                              e.stopPropagation();
                              setTooltipOpened(false);
                              setDeleteTarget(s);
                            }}
                          />
                        )}
                      </Row>
                    }
                  />
                );
              })}
              <Divider />
              <LeftBarSettingsMenuItem
                onClick={() => {
                  setDialogOpened(true)
                  setTooltipOpened(false);
                }}
                title={lang.menu.newSeasonButton}
                icon={<PlusIcon style={{ marginRight: 16 }} />}
              />
            </Tooltip>
          </div>
        ) : (
          <Row onClick={() => setDialogOpened(true)} alignItems="center">
            {expanded && (
              <Text type="heading" color="#494C5B" size={12}>
                {lang.seasons.createSeasonButton}
              </Text>
            )}
            <PlusIcon style={{ minWidth: 24 }} />
          </Row>
        )}
        <CreateSeasonDialog
          visible={dialogOpened}
          seasons={vm.seasons}
          onSubmit={(from, to, copyFromSeasonId) => {
            vm.createSeason(from, to, copyFromSeasonId).then(() => {
              setDialogOpened(false);
            }).catch((e) => {
              console.error("Failed to create season", e);
              toast.error(e.message ?? e.toString());
            });
          }}
          onClose={() => setDialogOpened(false)}
        />
        <EditSeasonDialog
          visible={editTarget !== null}
          initialFrom={editTarget?.from}
          initialTo={editTarget?.to}
          onSubmit={(from, to) => {
            if (!editTarget) return;
            vm.updateSeason(editTarget.id, from, to).then(() => {
              setEditTarget(null);
            }).catch((e) => {
              console.error("Failed to update season", e);
              toast.error(e.message ?? e.toString());
            });
          }}
          onClose={() => setEditTarget(null)}
        />
        <DeleteSeasonDialog
          visible={deleteTarget !== null}
          seasonName={deleteTarget ? seasonName(deleteTarget) : undefined}
          onSubmit={async () => {
            if (!deleteTarget) return;
            try {
              await vm.deleteSeason(deleteTarget.id);
              setDeleteTarget(null);
            } catch (err: any) {
              toast.error(err?.message ?? String(err));
            }
          }}
          onClose={() => setDeleteTarget(null)}
        />
      </Root>
    </div>
  );
});

const Seasons: React.FC<IProps> = (props) => {
  return <SeasonsImpl {...props} />;
}

export default observer(Seasons);
